import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface ProjectState {
  currentEmail: string;
  currentProjectId: string;
  currentProjectSlug: string;
  currentProjectName: string;
  currentServerId: string;
  currentServerName: string;
  currentChannelId: string;
  currentChannelName: string;
}

interface ProjectAction {
  updateEmail: (currentEmail: ProjectState["currentEmail"]) => void;
  updateProjectId: (currentProjectId: ProjectState["currentProjectId"]) => void;
  updateProjectSlug: ( currentProjectSlug: ProjectState["currentProjectSlug"]) => void;
  updateProjectName: (
    currentProjectName: ProjectState["currentProjectName"],
  ) => void;
  updateServerId: (currentServerId: ProjectState["currentServerId"]) => void;
  updateServerName: (
    currentServerName: ProjectState["currentServerName"],
  ) => void;
  updateChannelId: (currentChannelId: ProjectState["currentChannelId"]) => void;
  updateChannelName: (
    currentChannelName: ProjectState["currentChannelName"],
  ) => void;
}

export const useProjectStore = create(
  persist<ProjectState & ProjectAction>(
    (set) => ({
      currentEmail: "",
      currentProjectId: "",
        currentProjectSlug: "",
      currentProjectName: "",
      currentServerId: "",
      currentServerName: "",
      currentChannelId: "",
      currentChannelName: "",
      updateEmail: (email: string) => {
        set({ currentEmail: email });
      },
      updateProjectId: (projectId: string) => {
        set({ currentProjectId: projectId });
      },
        updateProjectSlug: (projectSlug: string) => {
            set({ currentProjectSlug: projectSlug });
        },
      updateProjectName: (projectName: string) => {
        set({ currentProjectName: projectName });
      },
      updateServerId: (serverId: string) => {
        set({ currentServerId: serverId });
      },
      updateServerName: (serverName: string) => {
        set({ currentServerName: serverName });
      },
      updateChannelId: (channelId: string) => {
        set({ currentChannelId: channelId });
      },

      updateChannelName: (channelName: string) => {
        set({ currentChannelName: channelName });
      },
    }),
    {
      name: "projectStore",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

interface SingleProjectState {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    cronSchedule: string | null;
    hrs: number | null;
    mins: number | null;
    channelId: string | null;
    createdAt: string;
    updatedAt: string;
    deleted: number;
    active: number;
    creatorId: string;
    tier: string | null;
    orgId: string;
    sendMemeOrNot: number;
    greetings: string | null;
    timezone: string | null;
    timezoneOffset: number | null;
    followLocalTimezone: number;
    weekdays: string | null;
}

interface SingleProjectAction {
    updateId: (id: SingleProjectState["id"]) => void;
    updateName: (name: SingleProjectState["name"]) => void;
    updateSlug: (slug: SingleProjectState["slug"]) => void;
    updateDescription: (description: SingleProjectState["description"]) => void;
    updateCronSchedule: (cronSchedule: SingleProjectState["cronSchedule"]) => void;
    updateHrs: (hrs: SingleProjectState["hrs"]) => void;
    updateMins: (mins: SingleProjectState["mins"]) => void;
    updateChannelId: (channelId: SingleProjectState["channelId"]) => void;
    updateCreatedAt: (createdAt: SingleProjectState["createdAt"]) => void;
    updateUpdatedAt: (updatedAt: SingleProjectState["updatedAt"]) => void;
    updateDeleted: (deleted: SingleProjectState["deleted"]) => void;
    updateActive: (active: SingleProjectState["active"]) => void;
    updateCreatorId: (creatorId: SingleProjectState["creatorId"]) => void;
    updateTier: (tier: SingleProjectState["tier"]) => void;
    updateOrgId: (orgId: SingleProjectState["orgId"]) => void;
    updateSendMemeOrNot: (sendMemeOrNot: SingleProjectState["sendMemeOrNot"]) => void;
    updateGreetings: (greetings: SingleProjectState["greetings"]) => void;
    updateTimezone: (timezone: SingleProjectState["timezone"]) => void;
    updateTimezoneOffset: (timezoneOffset: SingleProjectState["timezoneOffset"]) => void;
    updateFollowLocalTimezone: (followLocalTimezone: SingleProjectState["followLocalTimezone"]) => void;
    updateWeekdays: (weekdays: SingleProjectState["weekdays"]) => void;
}

export const useSingleProjectStore = create(
    persist<SingleProjectState & SingleProjectAction>(
        (set) => ({
            id: "",
            name: "",
            slug: "",
            description: null,
            cronSchedule: null,
            hrs: null,
            mins: null,
            channelId: null,
            createdAt: "",
            updatedAt: "",
            deleted: 0,
            active: 0,
            creatorId: "",
            tier: null,
            orgId: "",
            sendMemeOrNot: 0,
            greetings: null,
            timezone: null,
            timezoneOffset: null,
            followLocalTimezone: 0,
            weekdays: null,
            updateId: (id: string) => {
                set({ id: id });
            },
            updateName: (name: string) => {
                set({ name: name });
            },
            updateSlug: (slug: string) => {
                set({ slug: slug });
            },
            updateDescription: (description: string | null) => {
                set({ description: description });
            },
            updateCronSchedule: (cronSchedule: string | null) => {
                set({ cronSchedule: cronSchedule });
            },
            updateHrs: (hrs: number | null) => {
                set({ hrs: hrs });
            },
            updateMins: (mins: number | null) => {
                set({ mins: mins });
            },
            updateChannelId: (channelId: string | null) => {
                set({ channelId: channelId });
            },
            updateCreatedAt: (createdAt: string) => {
                set({ createdAt: createdAt });
            },
            updateUpdatedAt: (updatedAt: string) => {
                set({ updatedAt: updatedAt });
            },
            updateDeleted: (deleted: number) => {
                set({ deleted: deleted });
            },
            updateActive: (active: number) => {
                set({ active: active });
            },
            updateCreatorId: (creatorId: string) => {
                set({ creatorId: creatorId });
            },
            updateTier: (tier: string | null) => {
                set({ tier: tier });
            },
            updateOrgId: (orgId: string) => {
                set({ orgId: orgId });
            },
            updateSendMemeOrNot: (sendMemeOrNot: number) => {
                set({ sendMemeOrNot: sendMemeOrNot });
            },
            updateGreetings: (greetings: string | null) => {
                set({ greetings: greetings });
            },
            updateTimezone: (timezone: string | null) => {
                set({ timezone: timezone });
            },
            updateTimezoneOffset: (timezoneOffset: number | null) => {
                set({ timezoneOffset: timezoneOffset });
            },
            updateFollowLocalTimezone: (followLocalTimezone: number) => {
                set({ followLocalTimezone: followLocalTimezone });
            },
            updateWeekdays: (weekdays: string | null) => {
                set({ weekdays: weekdays });
            },

        }),
        {
            name: "singleProjectStore",
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
);

interface ProjectList {
    id?: string;
    name?: string;
    slug?: string;
    orgId?: string;
    archived? : number | null | undefined;
    deleted? : number | null | undefined;
}

interface ProjectKey {
    slug: string;
    orgId: string;

}
const initialProjectList = {
    id: "",
    name: "",
    slug: "",
    orgId: "",
    archived: 0,
    deleted: 0,
} as ProjectList;


interface ProjectListState {
    projectList: ProjectList[];
    addProject : (id: string, name: string, slug: string, orgId: string, deleted: number) => void;
    deleteProject : (slug: string, orgId: string) => void;
    toggleArchiveProject : (slug: string, orgId: string) => void;
    reset: () => void;
}

export const useProjectListStore = create(persist< ProjectList & ProjectListState>((set, get) => ({

    projectList: [],
    addProject: (id: string, name: string, slug: string, orgId: string, deleted: number) => {
        const existProject = Object.values(get().projectList).find((p) => p.slug === slug && p.orgId === orgId);

        if(!existProject){
            set((state) =>
                ({
                    ...state,
                 projectList: [ ...Object.values(state.projectList), {
                                id: id,
                                name: name,
                                slug: slug,
                                orgId: orgId,
                                archived: 0,
                                deleted: deleted
                                }]

                })
            )
        }
        return

    },
    deleteProject: (slug: string, orgId: string) => {

        set(state => ({
            projectList: state.projectList.filter((p) => {
               if(p.slug === slug && p.orgId === orgId) {
                   p.deleted = 1;
               }
               return p;
            })
        }))
    },
    toggleArchiveProject: (slug: string, orgId: string) => {
        set((state) => ({
            projectList: state.projectList.map((p) => {
                if(p.slug === slug && p.orgId === orgId) {
                    p.archived = p.archived === 1 ? 1 : 0;
                }
                return p;
            })

    }))},
    reset: () => set(() => ({
        projectList: []

    }))
}),
{
    name: "projectListStore",
    storage: createJSONStorage(() => sessionStorage),
}));